/**
 * The spring runtime — ADR-038 (Proposed): **run a declared spring chain
 * with the VRM reference solver, on a fixed clock.**
 *
 * ONE responsibility: turn a kernel {@link SpringDeclaration} into
 * @pixiv/three-vrm-springbone joints and colliders over the bones of a loaded
 * character, and advance them. The solver is the reference implementation of
 * `VRMC_springBone` 1.0 (MIT, pixiv) — the same arithmetic every VRM reader
 * runs — so the numbers Harry declares mean in this viewport what they mean
 * in the spec, and a World that reads the extension with the reference
 * algorithm shows the same motion.
 *
 * THE CLOCK. The reference update is per STEP (drag removes a share of the
 * Verlet velocity per step; stiffness and gravity are added as `power·dt`), so
 * driving it with the render's frame dt makes the motion depend on the frame
 * rate — measured in `tail-spring-probe.test.ts`. `advance(frameDt)` runs a
 * fixed-step accumulator instead (60 Hz by default, a capped number of
 * substeps after a stall), and when the caller can pose the character at a
 * substep's time (`poseAt`), every substep sees the pose of ITS time: the
 * trajectory at the step times is then the same at any render rate.
 * `stepRaw(dt)` is the frame-driven mode, kept for measurement only.
 *
 * NOTHING ENTERS THE SCENE. The reference loader parents its colliders under
 * the bones (Object3Ds the export would write as nodes); here the colliders
 * stay unparented and their matrices are copied from their bones before every
 * step, so the model subtree is exactly what was loaded — and the bones keep
 * their own `matrixAutoUpdate`, which the reference joint would switch off. The joints write
 * `bone.quaternion`; {@link SpringRuntime.hold} saves and restores them around
 * a render, the tail-wag's slot discipline, so every door that reads the scene
 * between frames (export, package, pose) reads the character at rest.
 *
 * Depends on: three, @pixiv/three-vrm-springbone, kernel (types).
 * Depended on by: app (the Body-tab spring), the spring probe.
 */
import { Matrix4, Quaternion, Vector3, type Object3D } from 'three';
import {
  VRMSpringBoneCollider,
  VRMSpringBoneColliderShapeCapsule,
  VRMSpringBoneColliderShapeSphere,
  VRMSpringBoneJoint,
  VRMSpringBoneManager,
} from '@pixiv/three-vrm-springbone';
import type { SpringDeclaration, SpringDynamics, TailHinge } from '@harryhausen/kernel';
import { AdapterError } from '../errors.js';

export class SpringRuntimeError extends AdapterError {}

/**
 * The reference joint's state this runtime re-seats (ADR-038 R5). TypeScript
 * `private` in three-vrm-springbone 3.5.5 (`lib/three-vrm-springbone.module.js`
 * :530–700): `_initialLocalMatrix` / `_initialLocalRotation` are the target the
 * update pulls toward and rebuilds its rotation from; `_currentTail` is the
 * Verlet state; `_initialLocalChildPosition` the bone's tail in its own frame.
 * No public API re-targets a joint without resetting its velocity
 * (`setInitState` also rewrites `_currentTail` / `_prevTail`), so the fields are
 * written directly — pinned by `spring-runtime.test.ts` against a version bump.
 */
interface ReferenceJoint {
  readonly bone: Object3D;
  readonly _initialLocalMatrix: Matrix4;
  readonly _initialLocalRotation: Quaternion;
  readonly _initialLocalChildPosition: Vector3;
  readonly _currentTail: Vector3;
  readonly _prevTail: Vector3;
  readonly _boneAxis: Vector3;
  _getMatrixWorldToCenter(): Matrix4;
  /** Pushes `tail` (world) out of every collider; reads the module's `_worldSpacePosition`, which this joint's `update` just set. */
  _collision(tail: Vector3): void;
  update(delta: number): void;
}

export interface SpringRuntimeOptions {
  /** Fixed simulation rate, Hz (default 60). */
  readonly stepHz?: number;
  /** Substeps allowed per advance; beyond it the lost time is dropped, never integrated (default 8). */
  readonly maxSubsteps?: number;
  /**
   * GROUND CONTACT (Allen, 2026-09-25: "no tail collision on ground or
   * surface platforms"): the plane under the character, asked every
   * collision. A joint's tail is pushed to its hit radius above the plane,
   * along the normal, AFTER the declared colliders — on every path (passive,
   * driven, the cone's re-collide), so it wins over a clip that poses the tail
   * underground. World applies the same rule from its controller's ground
   * contact; the viewport passes its floor. Null = no ground (airborne).
   */
  readonly ground?: () => { readonly point: Vector3; readonly normal: Vector3 } | null;
}

/**
 * The layered drive (ADR-038 R5, ruled 2026-09-24; `kernel/springs/spring-drive.ts`
 * holds the model and its numbers). Set on a runtime, every fixed step runs, at
 * the STEP's time: the animated pose sampled (what the bones hold after
 * `poseAt` — authored keys — or their rest where nothing keys them) → the target
 * (animated ⊕ the drive's mood offsets ⊕ the reflex) → the body's motion carried
 * or inherited (inertia, speed caps, the teleport reset) → the reference joint
 * update with its initial local frame RE-SEATED to the target (the library hook:
 * `VRMSpringBoneJoint._initialLocalMatrix` / `_initialLocalRotation`, the only
 * state the reference update reads as its target) → the muscle (a half-life
 * gain toward the target with a rate cap, written back into the Verlet state)
 * → the cone limit (overshoot cancelled) and the colliders again. The last
 * bone, which no joint moves, wears its target.
 */
export interface SpringRuntimeDrive {
  /** At `seconds`: each chain bone's local mood offset (root → tip, post-multiplied onto the animated rotation) and each sprung joint's gain in [0, 1]. */
  sample(seconds: number, offsets: Quaternion[], gains: number[]): void;
  readonly dynamics: SpringDynamics;
  /** The reflex (balance) layer on. */
  readonly reflex: boolean;
  /** A flat tail's hinge (ADR-038 §13.7): per sprung joint, the bone-local axis it bends about freely; off-axis bend and twist resisted. Null = none. */
  readonly hinge?: TailHinge | null;
  /** Per-axis response (ADR-038 J3, soft tissue): the SHOWN deviation from the target, scaled per axis. Null = isotropic. */
  readonly response?: SpringResponse | null;
  /**
   * Gravity falloff (ADR-038 J3; PhysBones' Gravity Falloff), 0–1: a joint's
   * gravity is scaled by `1 − falloff · (1 − min(1, off / cone))` each step,
   * `off` its angle from the target — 1 = no gravity at rest, all of it at the
   * cone. Absent / 0 = the declared gravity always.
   */
  readonly gravityFalloff?: number;
  /**
   * A SOFT cone (ADR-038 J3, 2026-09-27), 0–1: the share of the cone over
   * which a swing is COMPRESSED instead of stopped. Past `cone × (1 − soft)`
   * the angle maps through tanh onto the remaining band, so it approaches the
   * cone and never passes it, and the correction keeps `muscleMomentum` of
   * itself as velocity: the mass springs back off the limit. The ring sweep
   * measured the hard cone stopping a Firmness-0.4 breast dead 57 times in
   * three hops. Absent / 0 = the hard cone (set on it, velocity cancelled).
   */
  readonly softCone?: number;
}

/**
 * THE PER-AXIS RESPONSE (ADR-038 J3 — SL's Bounce / Sway, over a VRM joint
 * that is one isotropic spring). The physics runs unscaled; what a frame SHOWS
 * is the target ⊕ the deviation's rotation vector split on the joint's local
 * axes: `bounce` × its component about the bounce axis (the tip moves along
 * the body's up), `sway` × its component about the sway axis (the tip moves
 * sideways), and none of the rest (a mass does not roll about itself). An
 * output gain, never a feedback: scaling the Verlet state per step would damp
 * the axis instead of shrinking it.
 */
export interface SpringResponse {
  /** Per sprung joint, unit axes in the bone's LOCAL bind frame. */
  readonly axes: readonly { readonly bounce: readonly [number, number, number]; readonly sway: readonly [number, number, number] }[];
  readonly bounce: number;
  readonly sway: number;
}

export interface SpringRuntimeProbe {
  readonly steps: number;
  readonly droppedSeconds: number;
  readonly joints: number;
  readonly colliders: number;
  /** Driven steps that reset the chain on a body jump past the teleport thresholds. */
  readonly teleports: number;
  /** Joint-steps set back on their cone (velocity cancelled). */
  readonly coneClamps: number;
  /** Joint-steps whose muscle hit its rate cap (the tail losing the fight for a moment). */
  readonly saturated: number;
}

export interface SpringRuntime {
  readonly declaration: SpringDeclaration;
  /** The chain's bones, root → tip, as resolved in the scene. */
  readonly bones: readonly Object3D[];
  /**
   * Advance by one render frame through the fixed-step accumulator. `poseAt`
   * (optional) poses the character at an absolute simulation time before the
   * substep that runs at it. Returns the substeps run — and ALWAYS leaves the
   * chain on its latest solution, even when no substep was due this frame.
   */
  advance(frameDt: number, poseAt?: (seconds: number) => void): number;
  /** One raw solver step of `dt` — the frame-driven mode, for measurement only. */
  stepRaw(dt: number): void;
  /** Put every tail back on its rest and forget the velocity (teleport, clip change). */
  reset(): void;
  /**
   * Give every joint's tail a world VELOCITY (m/s): the Verlet state's previous
   * tail moves back by `velocity × dt`. The feedback kick (2026-09-27, Allen:
   * "making the slider adjustment bounces the soft tissue — that bounce becomes
   * the visual reinforcement"): a still body shows its new settings at once.
   */
  kick(velocity: { readonly x: number; readonly y: number; readonly z: number }): void;
  /**
   * New per-joint numbers for the SAME chain (a knob moved): the joints keep
   * their state, so a slider drag reads as the tail changing, not snapping.
   * A declaration over other bones or other colliders refuses by name.
   */
  retune(next: SpringDeclaration): void;
  /**
   * Layer the spring over a drive (the target follows the animation); null =
   * the plain reference spring against the rest. A spring evaluated in a
   * `center` space refuses a drive (the drive's inertia is its own center).
   */
  setDrive(drive: SpringRuntimeDrive | null): void;
  /**
   * A scrub or a cut: reset onto the target and run `seconds` of steps with the
   * pose held where it stands (the pre-roll), so a paused frame shows a settled
   * tail, not a whip. The step count and clock advance.
   */
  preroll(seconds: number): void;
  /** The drive's per-bone local rotations at the last step (null before a driven step) — the base-follow instrument. */
  lastDrive(): readonly Quaternion[] | null;
  /** Save the chain's quaternions; the returned function restores them bit-for-bit and refreshes the world. */
  hold(): () => void;
  /** The simulation clock, seconds (step count / stepHz). */
  readonly time: number;
  probe(): SpringRuntimeProbe;
}

/** A bone by the name the file speaks, or the session's `_graft` twin. */
export function findSpringBone(root: Object3D, name: string): Object3D | undefined {
  return root.getObjectByName(name) ?? root.getObjectByName(`${name}_graft`);
}

/**
 * Build the runtime over `root` (the loaded model subtree). The scene must be
 * AT REST when this is called: the reference joints capture their initial
 * local frames here. Throws {@link SpringRuntimeError} naming any bone the
 * declaration names and the scene does not carry.
 */
export function createSpringRuntime(root: Object3D, declaration: SpringDeclaration, options: SpringRuntimeOptions = {}): SpringRuntime {
  const stepHz = options.stepHz ?? 60;
  const maxSubsteps = options.maxSubsteps ?? 8;
  const dt = 1 / stepHz;
  const missing: string[] = [];
  const resolve = (name: string): Object3D => {
    const node = findSpringBone(root, name);
    if (node === undefined) missing.push(name);
    return node!;
  };
  const bones = declaration.joints.map((j) => resolve(j.bone));
  const colliderBones = declaration.colliders.map((c) => resolve(c.bone));
  if (missing.length > 0) {
    throw new SpringRuntimeError(`spring "${declaration.name}" names bones this character does not carry: ${[...new Set(missing)].join(', ')}`);
  }

  // Colliders: unparented, fed their bone's world matrix before every step.
  const colliders = declaration.colliders.map((c) => {
    const shape =
      'sphere' in c.shape
        ? new VRMSpringBoneColliderShapeSphere({ offset: new Vector3(...c.shape.sphere.offset), radius: c.shape.sphere.radius })
        : new VRMSpringBoneColliderShapeCapsule({
            offset: new Vector3(...c.shape.capsule.offset),
            radius: c.shape.capsule.radius,
            tail: new Vector3(...c.shape.capsule.tail),
          });
    const collider = new VRMSpringBoneCollider(shape);
    collider.matrixAutoUpdate = false;
    return collider;
  });
  const group = { colliders, name: declaration.name };

  root.updateMatrixWorld(true);
  // The reference joint turns its bone's matrixAutoUpdate OFF (VRMSpringBoneJoint's
  // constructor). Left that way, every other door that turns these bones — the
  // pose tool, the wag, the tail census — would change a quaternion the world
  // never sees (measured in the tail e2e, 2026-09-23: 57 of 598 tail vertices
  // moved under the census's turn). The solver itself calls updateMatrix()
  // before and after each joint, so auto-update is restored here.
  const autoUpdate = bones.map((b) => b.matrixAutoUpdate);
  const manager = new VRMSpringBoneManager();
  for (let k = 0; k + 1 < bones.length; k++) {
    const j = declaration.joints[k]!;
    manager.addJoint(
      new VRMSpringBoneJoint(
        bones[k]!,
        bones[k + 1]!,
        { hitRadius: j.hitRadius, stiffness: j.stiffness, gravityPower: j.gravityPower, gravityDir: new Vector3(...j.gravityDir), dragForce: j.dragForce },
        colliders.length > 0 ? [group] : [],
      ),
    );
  }
  if (declaration.center !== undefined) {
    const center = resolve(declaration.center);
    for (const joint of manager.joints) joint.center = center;
  }
  bones.forEach((b, k) => (b.matrixAutoUpdate = autoUpdate[k]!));
  manager.setInitState();
  const joints = [...manager.joints] as unknown as ReferenceJoint[];
  const ground = options.ground;
  if (ground !== undefined) {
    const head = new Vector3();
    const flat = new Vector3();
    for (const j of joints) {
      const declared = j._collision.bind(j);
      const own = j as unknown as { settings: { hitRadius: number }; bone: Object3D; _worldSpaceBoneLength: number };
      j._collision = (tail: Vector3): void => {
        declared(tail);
        const g = ground();
        if (g === null) return;
        const want = own.settings.hitRadius;
        const height = (tail.x - g.point.x) * g.normal.x + (tail.y - g.point.y) * g.normal.y + (tail.z - g.point.z) * g.normal.z;
        if (height >= want) return;
        // Exactly on the bone-length sphere round the head (as the library leaves
        // every collider's push), at the hit radius above the plane, keeping the
        // tail's heading along the plane. A head too low to reach: straight up.
        head.setFromMatrixPosition(own.bone.matrixWorld);
        const L = own._worldSpaceBoneLength;
        const up = want - ((head.x - g.point.x) * g.normal.x + (head.y - g.point.y) * g.normal.y + (head.z - g.point.z) * g.normal.z);
        flat.copy(tail).sub(head);
        flat.addScaledVector(g.normal, -flat.dot(g.normal));
        if (Math.abs(up) >= L || flat.lengthSq() === 0) {
          tail.copy(head).addScaledVector(g.normal, Math.sign(up) * L);
          return;
        }
        flat.normalize().multiplyScalar(Math.sqrt(L * L - up * up));
        tail.copy(head).addScaledVector(g.normal, up).add(flat);
      };
    }
  }
  // The rest (bind) targets, kept so reset() and a cleared drive return to them.
  const restLocalMatrix = joints.map((j) => j._initialLocalMatrix.clone());
  const restLocalRotation = joints.map((j) => j._initialLocalRotation.clone());
  const restQuaternions = bones.map((b) => b.quaternion.clone());
  const jointBones = new Set<Object3D>(bones.slice(0, -1));

  const chainParent = bones[0]!.parent;
  const refreshColliders = (): void => {
    colliders.forEach((c, i) => {
      c.matrix.copy(colliderBones[i]!.matrixWorld);
      // three r185 recomposes a world matrix only when it is flagged dirty
      // (or forced); a hand-fed matrix is not flagged by updateMatrix(), so
      // without this the collider sat at the origin — measured, 2026-09-23.
      c.matrixWorldNeedsUpdate = true;
      c.updateWorldMatrix(false, false);
    });
  };
  const driveQ = bones.map(() => new Quaternion());
  const offsetQ = bones.map(() => new Quaternion());
  const gains = joints.map(() => 0);
  let drive: SpringRuntimeDrive | null = null;
  let droveOnce = false;
  // The body (the chain's parent) at the previous driven step, and the reflex's smoothed angular velocity (world, rad/s).
  let bodyPrev: { p: Vector3; a: Matrix4 } | null = null;
  const omega = new Vector3();
  let teleports = 0;
  let coneClamps = 0;
  let saturated = 0;
  const bodyP = new Vector3();
  const bodyA = new Matrix4();
  const tmpM = new Matrix4();
  const tmpM2 = new Matrix4();
  const dq = new Quaternion();
  const carryQ = new Quaternion();
  const tmpQ = new Quaternion();
  const tmpV = new Vector3();
  const tail = new Vector3();
  const aimInv = new Matrix4();
  const hingeRel = new Quaternion();
  const hingeTwist = new Quaternion();
  const hingeSwing = new Quaternion();
  const hingeAxis = new Vector3();
  const identityQ = new Quaternion();
  /** The non-joint descendants of a joint's bone, world matrices refreshed (the reference manager's `_relevantChildrenUpdated`). */
  const updateNonJointChildren = (o: Object3D): void => {
    for (const c of o.children) {
      if (jointBones.has(c)) continue;
      c.updateWorldMatrix(false, false);
      updateNonJointChildren(c);
    }
  };
  const worldFromLocal = (bone: Object3D): void => {
    bone.updateMatrix();
    if (bone.parent !== null) bone.matrixWorld.multiplyMatrices(bone.parent.matrixWorld, bone.matrix);
    else bone.matrixWorld.copy(bone.matrix);
  };
  /** The reference joint's own rotation rule, aimed at a world tail (after a collision moved it). */
  const aimAt = (j: ReferenceJoint, bone: Object3D, world: Vector3): void => {
    aimInv.multiplyMatrices(bone.parent !== null ? bone.parent.matrixWorld : aimInv.identity(), j._initialLocalMatrix).invert();
    bone.quaternion.setFromUnitVectors(j._boneAxis, tmpV.copy(world).applyMatrix4(aimInv).normalize()).premultiply(j._initialLocalRotation);
    worldFromLocal(bone);
  };
  /** Carry a Verlet point along with the body by the share of its motion the tail does not inherit. */
  const carry = (pt: Vector3, from: Vector3, moved: Vector3, share: number, rot: Quaternion): void => {
    pt.sub(from).applyQuaternion(rot).add(from).addScaledVector(moved, share);
  };
  /** One driven step: the layered model, joint by joint root → tip. */
  const drivenUpdate = (h: number, time: number, anim: readonly Quaternion[]): void => {
    const d = drive!;
    const dyn = d.dynamics;
    // THE BODY: how far the chain's parent moved and turned since the last driven step.
    // Frame-honest under a mirroring ancestor (det −1, e.g. Babylon's glTF __root__): the turn is
    // the LINEAR-part product A_now · A_prev⁻¹ — a proper rotation even when each A mirrors — never
    // a decomposed quaternion (which drops the mirror into a scale sign).
    if (chainParent !== null) {
      bodyA.copy(chainParent.matrixWorld).setPosition(0, 0, 0);
      bodyP.setFromMatrixPosition(chainParent.matrixWorld);
    } else {
      bodyA.identity();
      bodyP.set(0, 0, 0);
    }
    let teleport = false;
    if (bodyPrev !== null) {
      dq.setFromRotationMatrix(tmpM.multiplyMatrices(bodyA, tmpM2.copy(bodyPrev.a).invert()).extractRotation(tmpM));
      if (dq.w < 0) dq.set(-dq.x, -dq.y, -dq.z, -dq.w);
      const angle = 2 * Math.acos(Math.min(1, dq.w));
      const moved = tmpV.copy(bodyP).sub(bodyPrev.p);
      const dist = moved.length();
      teleport = dist > dyn.teleportMeters || angle > (dyn.teleportDeg * Math.PI) / 180;
      if (!teleport) {
        // The reflex's angular velocity (world), smoothed per step.
        const sinHalf = Math.sqrt(Math.max(0, 1 - dq.w * dq.w));
        const target = sinHalf < 1e-9 ? new Vector3() : new Vector3(dq.x, dq.y, dq.z).multiplyScalar(angle / sinHalf / h);
        omega.lerp(target, 1 - Math.pow(2, -h / dyn.reflex.smoothHalfLifeS));
        // INERTIA: the tail inherits `inertia` of the body's motion up to the speed caps; the rest is carried.
        const inheritAngle = Math.min(dyn.inertia * angle, (dyn.maxInheritDegPerS * Math.PI * h) / 180);
        const inheritDist = Math.min(dyn.inertia * dist, dyn.maxInheritSpeed * h);
        const carriedTurn = angle < 1e-12 ? 0 : 1 - inheritAngle / angle;
        const carriedMove = dist < 1e-12 ? 0 : 1 - inheritDist / dist;
        if (carriedTurn > 0 || carriedMove > 0) {
          carryQ.identity().slerp(dq, carriedTurn);
          const from = bodyPrev.p;
          for (const j of joints) {
            carry(j._currentTail, from, moved, carriedMove, carryQ);
            carry(j._prevTail, from, moved, carriedMove, carryQ);
          }
        }
      }
    }
    if (teleport) omega.set(0, 0, 0);
    bodyPrev = { p: bodyP.clone(), a: bodyA.clone() };

    // THE TARGET: animated (what the bones hold now) ⊕ the mood's offsets ⊕ the reflex.
    for (const q of offsetQ) q.identity();
    gains.fill(0);
    d.sample(time, offsetQ, gains);
    bones.forEach((_, k) => driveQ[k]!.copy(anim[k]!).multiply(offsetQ[k]!));
    if (d.reflex && dyn.reflex.gainS > 0 && omega.lengthSq() > 0) {
      const r = tmpV.copy(omega).multiplyScalar(-dyn.reflex.gainS);
      const maxRad = (dyn.reflex.maxDeg * Math.PI) / 180;
      if (r.length() > maxRad) r.setLength(maxRad);
      const total = r.length();
      const axis = r.clone().normalize();
      const J = Math.min(dyn.reflex.joints, bones.length);
      const shareSum = (J * (J + 1)) / 2;
      // The world rotation R seen in bone k's frame is the matrix CONJUGATION Mk⁻¹·R·Mk (its
      // linear part) — exact with or without a mirroring ancestor, where a quaternion product of
      // world orientations is not (World measured 222 mm under __root__'s det −1; poqpoq-world #2045).
      const m = chainParent !== null ? chainParent.matrixWorld.clone() : new Matrix4();
      for (let k = 0; k < J; k++) {
        const bone = bones[k]!;
        const mk = m.clone().multiply(tmpM.compose(bone.position, driveQ[k]!, bone.scale));
        if (total > 0) {
          const rWorld = tmpM.makeRotationAxis(axis, (total * (J - k)) / shareSum);
          const x = mk.clone().invert().multiply(rWorld).multiply(mk);
          driveQ[k]!.multiply(tmpQ.setFromRotationMatrix(tmpM2.extractRotation(x)));
          mk.copy(m).multiply(tmpM.compose(bone.position, driveQ[k]!, bone.scale));
        }
        m.copy(mk);
      }
    }
    droveOnce = true;
    bones[bones.length - 1]!.quaternion.copy(driveQ[bones.length - 1]!);

    if (teleport) {
      // RESET: the chain onto its target, velocity forgotten.
      teleports += 1;
      joints.forEach((j, k) => {
        const bone = bones[k]!;
        j._initialLocalRotation.copy(driveQ[k]!);
        j._initialLocalMatrix.compose(bone.position, driveQ[k]!, bone.scale);
        bone.quaternion.copy(driveQ[k]!);
        worldFromLocal(bone);
        bone.localToWorld(j._currentTail.copy(j._initialLocalChildPosition));
        j._prevTail.copy(j._currentTail);
        updateNonJointChildren(bone);
      });
      return;
    }

    const maxStep = (dyn.maxDegPerS * Math.PI * h) / 180;
    const cone = (dyn.coneDeg * Math.PI) / 180;
    const vCap = dyn.maxParticleSpeed * h;
    joints.forEach((j, k) => {
      const bone = bones[k]!;
      // THE HOOK: the reference update's target is its initial local frame; the target IS that frame now.
      j._initialLocalRotation.copy(driveQ[k]!);
      j._initialLocalMatrix.compose(bone.position, driveQ[k]!, bone.scale);
      bone.updateMatrix();
      bone.updateWorldMatrix(false, false);
      // The particle's speed, capped before it integrates.
      const v = tmpV.subVectors(j._currentTail, j._prevTail);
      if (v.length() > vCap) j._prevTail.copy(j._currentTail).sub(v.setLength(vCap));
      const falloff = d.gravityFalloff ?? 0;
      if (falloff > 0) {
        const gOwn = (j as unknown as { settings: { gravityPower: number } }).settings;
        const off = bone.quaternion.angleTo(driveQ[k]!);
        gOwn.gravityPower = current.joints[k]!.gravityPower * (1 - falloff * (1 - Math.min(1, off / cone)));
      }
      j.update(h);
      let changed = false;
      let clamped = false;
      // THE MUSCLE: close a half-life share of the angle to the target, at most the budget.
      const g = gains[k]!;
      const theta = bone.quaternion.angleTo(driveQ[k]!);
      if (g > 0 && theta > 1e-9) {
        const want = theta * (1 - Math.pow(2, (-h * g) / dyn.tightHalfLifeS));
        if (want > maxStep) saturated += 1;
        bone.quaternion.rotateTowards(driveQ[k]!, Math.min(want, maxStep));
        changed = true;
      }
      // THE HINGE (a paddle, §13.7): the deviation from the target, split about the joint's hinge axis —
      // the hinge part free up to maxDeg, the rest (sideways bend, twist) pulled back and capped.
      const hg = d.hinge;
      if (hg !== null && hg !== undefined) {
        const rel = hingeRel.copy(driveQ[k]!).invert().multiply(bone.quaternion);
        if (rel.w < 0) rel.set(-rel.x, -rel.y, -rel.z, -rel.w);
        const ax = hingeAxis.set(...hg.axes[k]!);
        const dot = rel.x * ax.x + rel.y * ax.y + rel.z * ax.z;
        const twist = hingeTwist.set(ax.x * dot, ax.y * dot, ax.z * dot, rel.w);
        if (twist.lengthSq() < 1e-24) twist.identity();
        else twist.normalize();
        const swing = hingeSwing.copy(rel).multiply(tmpQ.copy(twist).invert());
        const hingeMax = (hg.maxDeg * Math.PI) / 180;
        const hingeAngle = 2 * Math.acos(Math.min(1, Math.abs(twist.w)));
        let limited = false;
        if (hingeAngle > hingeMax) {
          twist.slerp(identityQ, 1 - hingeMax / hingeAngle);
          limited = true;
        }
        const off0 = 2 * Math.acos(Math.min(1, Math.abs(swing.w)));
        if (off0 > 1e-12) {
          let keep = Math.pow(2, -h / hg.offAxisHalfLifeS);
          const offMax = (hg.offAxisMaxDeg * Math.PI) / 180;
          if (off0 * keep > offMax) {
            keep = offMax / off0;
            limited = true;
          }
          swing.copy(identityQ.clone().slerp(swing, keep));
        }
        bone.quaternion.copy(driveQ[k]!).multiply(swing).multiply(twist);
        changed = true;
        if (limited) clamped = true;
      }
      // THE CONE: past it, set on it and cancel the velocity (absorbed, not bounced) —
      // or, soft, compressed onto the band below it with the momentum kept.
      const off = bone.quaternion.angleTo(driveQ[k]!);
      const softShare = Math.min(0.95, Math.max(0, d.softCone ?? 0));
      const knee = cone * (1 - softShare);
      if (softShare > 0 && off > knee) {
        const band = cone - knee;
        const shown = knee + band * Math.tanh((off - knee) / band);
        bone.quaternion.rotateTowards(driveQ[k]!, off - shown);
        coneClamps += 1;
        changed = true;
      } else if (off > cone) {
        bone.quaternion.rotateTowards(driveQ[k]!, off - cone);
        coneClamps += 1;
        changed = true;
        clamped = true;
      }
      if (changed) {
        worldFromLocal(bone);
        bone.localToWorld(tail.copy(j._initialLocalChildPosition));
        const before = tmpV.copy(tail);
        j._collision(tail);
        if (!tail.equals(before)) aimAt(j, bone, tail);
        // The muscle's correction moves the Verlet state; `muscleMomentum` of it
        // becomes velocity (prev moves by the rest), so a driven base neither
        // rings about its target (all of it) nor drags its distal joints along
        // with no momentum (none of it). A cone clamp cancels the velocity outright.
        if (clamped) j._prevTail.copy(tail);
        else j._prevTail.addScaledVector(tmpV.subVectors(tail, j._currentTail), 1 - dyn.muscleMomentum);
        j._currentTail.copy(tail);
      }
      updateNonJointChildren(bone);
    });
  };
  /** The chain's animated pose now: each bone as posed, unless it still wears this runtime's own solution (nothing posed it since) — then `fallback[k]`. */
  const animatedNow = (fallback: readonly Quaternion[]): Quaternion[] =>
    bones.map((b, k) =>
      // Its own work is the last solution (between substeps) or what the last frame SHOWED (a response scales it).
      ((solved !== null && b.quaternion.equals(solved[k]!)) || (shown !== null && b.quaternion.equals(shown[k]!)) ? fallback[k]! : b.quaternion).clone(),
    );
  const step = (h: number, anim?: readonly Quaternion[]): void => {
    // The parent chain and the legs are posed by whoever drives the clip; make
    // their world matrices current, then the colliders', then solve.
    chainParent?.updateWorldMatrix(true, true);
    for (const b of colliderBones) b.updateWorldMatrix(true, false);
    refreshColliders();
    if (drive === null) manager.update(h);
    else drivenUpdate(h, (steps + 1) * dt, anim ?? animatedNow(restQuaternions));
    steps += 1;
    solved = bones.map((b) => b.quaternion.clone());
  };
  /**
   * The chain's latest solution, written back onto the bones. A render frame
   * that runs NO substep (every other frame at 120 Hz, 58% of them at 144 Hz
   * against a 60 Hz step) would otherwise draw the chain wherever the frame
   * left it — at rest, since the slot restores the bones after every render —
   * and the eye sees the sprung tail and the original tail flickering as TWO
   * tails (Allen's field read on :5192, 2026-09-23).
   */
  let solved: Quaternion[] | null = null;
  /** What the bones last WORE — the solution, or its per-axis response when the drive declares one. */
  let shown: Quaternion[] | null = null;
  const respV = new Vector3();
  const respAx = new Vector3();
  const respQ = new Quaternion();
  const showSolved = (): void => {
    if (solved === null) return;
    const r = drive?.response ?? null;
    shown = bones.map((_, k) => {
      const q = solved![k]!;
      if (r === null || !droveOnce || k >= joints.length) return q.clone();
      // The deviation from the target as a rotation vector, split on the joint's axes.
      respQ.copy(driveQ[k]!).invert().multiply(q);
      if (respQ.w < 0) respQ.set(-respQ.x, -respQ.y, -respQ.z, -respQ.w);
      const sinHalf = Math.sqrt(Math.max(0, 1 - respQ.w * respQ.w));
      if (sinHalf < 1e-12) return driveQ[k]!.clone();
      respV.set(respQ.x, respQ.y, respQ.z).multiplyScalar((2 * Math.acos(Math.min(1, respQ.w))) / sinHalf);
      const ax = r.axes[k]!;
      const vb = respV.dot(respAx.set(...ax.bounce));
      const out = respAx.clone().multiplyScalar(vb * r.bounce);
      const vs = respV.dot(respAx.set(...ax.sway));
      out.addScaledVector(respAx, vs * r.sway);
      const angle = out.length();
      return angle < 1e-12 ? driveQ[k]!.clone() : driveQ[k]!.clone().multiply(respQ.setFromAxisAngle(out.normalize(), angle));
    });
    bones.forEach((b, k) => b.quaternion.copy(shown![k]!));
    chainParent?.updateWorldMatrix(false, true);
  };

  let current = declaration;
  let steps = 0;
  let accumulator = 0;
  let droppedSeconds = 0;
  const api: SpringRuntime = {
    get declaration() {
      return current;
    },
    bones,
    get time() {
      return steps * dt;
    },
    advance(frameDt, poseAt) {
      if (!(frameDt > 0)) return 0;
      accumulator += frameDt;
      let n = 0;
      // A driven step samples the ANIMATED pose: what the chain held when this
      // frame began — unless that is the runtime's own last solution (nothing
      // posed the chain since: no clip keys it), which means its rest. A
      // substep's `poseAt` that keys the chain overrides it; the bones
      // themselves are never rewritten before the solve.
      const frameAnim = drive === null ? null : animatedNow(restQuaternions);
      while (accumulator >= dt - 1e-12) {
        if (n === maxSubsteps) {
          droppedSeconds += accumulator;
          accumulator = 0;
          break;
        }
        accumulator -= dt;
        poseAt?.((steps + 1) * dt);
        step(dt, frameAnim === null ? undefined : animatedNow(frameAnim));
        n += 1;
      }
      // Stepped or not, the chain leaves this call wearing its latest solution.
      showSolved();
      return n;
    },
    stepRaw(h) {
      if (h > 0) step(h);
    },
    kick(velocity) {
      for (const j of joints) {
        j._prevTail.x -= velocity.x * dt;
        j._prevTail.y -= velocity.y * dt;
        j._prevTail.z -= velocity.z * dt;
      }
    },
    reset() {
      chainParent?.updateWorldMatrix(true, true);
      // Back to the REST targets (a drive re-seats them each step), then the reference reset.
      joints.forEach((j, k) => {
        j._initialLocalMatrix.copy(restLocalMatrix[k]!);
        j._initialLocalRotation.copy(restLocalRotation[k]!);
      });
      bones[bones.length - 1]!.quaternion.copy(restQuaternions[bones.length - 1]!);
      bodyPrev = null;
      omega.set(0, 0, 0);
      manager.reset();
      accumulator = 0;
      solved = null;
      shown = null;
    },
    retune(next) {
      const same =
        next.joints.length === declaration.joints.length &&
        next.joints.every((j, k) => j.bone === declaration.joints[k]!.bone) &&
        JSON.stringify(next.colliders) === JSON.stringify(declaration.colliders);
      if (!same) throw new SpringRuntimeError(`spring "${declaration.name}": a retune must keep the chain and its colliders — build a new runtime`);
      [...manager.joints].forEach((joint) => {
        const k = bones.indexOf(joint.bone);
        const j = next.joints[k]!;
        joint.settings.stiffness = j.stiffness;
        joint.settings.dragForce = j.dragForce;
        joint.settings.gravityPower = j.gravityPower;
        joint.settings.gravityDir.set(...j.gravityDir);
        joint.settings.hitRadius = j.hitRadius;
      });
      current = next;
    },
    setDrive(next) {
      if (next !== null && declaration.center !== undefined) {
        throw new SpringRuntimeError(`spring "${declaration.name}" is evaluated in "${declaration.center}"'s space — the layered drive carries its own body frame and refuses a center`);
      }
      if (next !== null && next.dynamics.stepHz !== stepHz) {
        throw new SpringRuntimeError(`spring "${declaration.name}": the drive declares ${next.dynamics.stepHz} Hz, this runtime steps at ${stepHz} Hz`);
      }
      if (next?.response != null && next.response.axes.length !== joints.length) {
        throw new SpringRuntimeError(`spring "${declaration.name}": the response declares ${next.response.axes.length} joint axes, the chain has ${joints.length} sprung joints`);
      }
      bodyPrev = null;
      omega.set(0, 0, 0);
      // A falloff drive rewrote each joint's gravity per step; the declaration's is the start of any other drive.
      joints.forEach((j, k) => {
        (j as unknown as { settings: { gravityPower: number } }).settings.gravityPower = current.joints[k]!.gravityPower;
      });
      if (next === null) {
        joints.forEach((j, k) => {
          j._initialLocalMatrix.copy(restLocalMatrix[k]!);
          j._initialLocalRotation.copy(restLocalRotation[k]!);
        });
        droveOnce = false;
      }
      drive = next;
    },
    preroll(seconds) {
      // The pose to hold: what the chain wears now, unless that is the runtime's own solution (then its rest).
      const hold = animatedNow(restQuaternions);
      api.reset();
      const n = Math.round(seconds * stepHz);
      for (let i = 0; i < n; i++) step(dt, hold);
      showSolved();
    },
    lastDrive() {
      return drive !== null && droveOnce ? driveQ.map((q) => q.clone()) : null;
    },
    hold() {
      const saved = bones.map((b) => b.quaternion.clone());
      return () => {
        bones.forEach((b, k) => b.quaternion.copy(saved[k]!));
        bones[0]!.updateMatrixWorld(true);
      };
    },
    probe: () => ({ steps, droppedSeconds, joints: manager.joints.size, colliders: colliders.length, teleports, coneClamps, saturated }),
  };
  return api;
}
